/**
 * The app shell: sign-in gates, pages by permission, the licence lock
 * and evaluation banner.
 *
 * @module
 */
import { useEffect, useMemo, useState } from "react";
import AppProvider from "@atl/ui/providers/AppProvider/AppProvider";
import DashboardLayout from "@atl/ui/components/DashboardLayout";
import ThemeSwitcher from "@atl/ui/components/ThemeSwitcher";
import type { Navigation } from "@atl/ui/types/navigation";
import type { Router } from "@atl/ui/types/router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import type { Permission, SessionInfo } from "./api/client";
import { AlertsPage, useOpenAlerts } from "./audit/AlertsPage";
import { AuditPage } from "./audit/AuditPage";
import { AccountPage } from "./auth/AccountPage";
import { ChangePasswordPage } from "./auth/ChangePasswordPage";
import { MfaEnrolPage, MfaVerifyPage } from "./auth/MfaPages";
import { CasesPage } from "./cases/CasesPage";
import { LoginPage } from "./auth/LoginPage";
import { SetupPage } from "./auth/SetupPage";
import {
  hasPermission,
  useLogout,
  useSession,
  useSetupStatus,
} from "./auth/session";
import { AnalyticsPage } from "./admin/AnalyticsPage";
import { IntegrityPage } from "./admin/IntegrityPage";
import { RetentionSettingsPage } from "./admin/RetentionSettingsPage";
import { SettingsPage } from "./admin/SettingsPage";
import { DevicesPage } from "./admin/DevicesPage";
import { MapsPage } from "./admin/MapsPage";
import { ProfilesPage } from "./admin/ProfilesPage";
import { TeamsPage } from "./admin/TeamsPage";
import { UsersPage } from "./admin/UsersPage";
import { AboutPage } from "./help/AboutPage";
import { hoursLeft, LicencePage, useLicence } from "./licence/LicencePage";
import { HelpPage } from "./help/HelpPage";
import { HomePage } from "./home/HomePage";
import { ImportPage } from "./import/ImportPage";
import { goTo } from "./nav";
import { RetentionPage } from "./retention/RetentionPage";
import { NeedsReviewPage } from "./review/NeedsReviewPage";
import { TimelinePage } from "./timeline/TimelinePage";
import { PlaybackPage } from "./timeline/PlaybackPage";
import { UploadIndicator } from "./import/UploadIndicator";
import { theme } from "./theme";

/** A page shows if the user holds any of `requires`. */
const PAGES: {
  href: string;
  title: string;
  requires?: Permission | Permission[];
  /** Hidden in the desktop app's simple mode. */
  advanced?: boolean;
}[] = [
  { href: "/timeline", title: "Timeline", requires: "View" },
  { href: "/playback", title: "Playback", requires: "View" },
  { href: "/review", title: "Review" },
  { href: "/cases", title: "Cases", requires: "View" },
  {
    href: "/retention",
    title: "Retention",
    requires: ["Delete", "ApproveDisposal", "LegalHold", "ViewAudit"],
  },
  { href: "/import", title: "Import", requires: "Import" },
  { href: "/needs-review", title: "Needs review", requires: "Import" },
  { href: "/users", title: "Users", requires: "ManageUsers" },
  { href: "/teams", title: "Teams", requires: "ManageTeams", advanced: true },
  {
    href: "/profiles",
    title: "Filename profiles",
    requires: ["ManageSources", "Import"],
  },
  { href: "/maps", title: "Maps", requires: "ManageMaps" },
  { href: "/devices", title: "Devices", requires: "ManageSources" },
  { href: "/analytics", title: "Analytics", requires: "ManageSystem" },
  { href: "/audit", title: "Audit log", requires: "ViewAudit" },
  { href: "/alerts", title: "Alerts", requires: "ViewAudit" },
  {
    href: "/integrity",
    title: "Integrity",
    requires: ["ViewAudit", "ManageSystem"],
  },
  {
    href: "/retention-settings",
    title: "Retention settings",
    requires: "ManageRetention",
  },
  { href: "/settings", title: "Settings", requires: "ManageSystem" },
  { href: "/licence", title: "Licence", requires: "ManageSystem" },
  { href: "/account", title: "My account" },
  { href: "/user-guide", title: "Help" },
  { href: "/about", title: "About" },
];

/** All that works while the evaluation is over or the licence doesn't cover this build. */
const LOCKED_PAGES = ["/licence", "/user-guide", "/about"];

/** The page the URL names, so a reload stays put; unknown paths go to the Timeline. */
function pageFor(path: string): string {
  return PAGES.some((p) => p.href === path) ? path : "/timeline";
}

export function App({ nonce }: { nonce?: string }) {
  const [pathname, setPathname] = useState(() =>
    pageFor(window.location.pathname),
  );
  useEffect(() => {
    const back = () => setPathname(pageFor(window.location.pathname));
    window.addEventListener("popstate", back);
    return () => window.removeEventListener("popstate", back);
  }, []);
  // Atlantic UI only needs a router-shaped object; the server serves the SPA on every page path.
  const router = useMemo<Router>(
    () => ({
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (url) => {
        const next = pageFor(
          new URL(String(url), window.location.href).pathname,
        );
        if (next !== window.location.pathname)
          window.history.pushState(null, "", next);
        setPathname(next);
      },
    }),
    [pathname],
  );
  return (
    <AppProvider theme={theme} router={router} nonce={nonce}>
      <Content pathname={pathname} />
    </AppProvider>
  );
}

function Content({ pathname }: { pathname: string }) {
  const session = useSession();
  const setup = useSetupStatus(session.data === null);
  if (session.isPending || (session.data === null && setup.isPending)) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }
  if (session.isError) {
    return (
      <Alert severity="error">
        Cannot reach the AVGV server: {session.error.message}
      </Alert>
    );
  }
  if (!session.data) return setup.data?.needed ? <SetupPage /> : <LoginPage />;
  if (session.data.mfa === "enrol") return <MfaEnrolPage />;
  if (session.data.mfa === "required") return <MfaVerifyPage />;
  if (session.data.must_change_password) return <ChangePasswordPage forced />;
  return <Shell session={session.data} pathname={pathname} />;
}

function Shell({
  session,
  pathname,
}: {
  session: SessionInfo;
  pathname: string;
}) {
  const logout = useLogout();
  const licence = useLicence();
  const locked = licence.data?.locked ?? false;
  const allowed = PAGES.filter((p) =>
    locked
      ? LOCKED_PAGES.includes(p.href)
      : !(p.advanced && session.simple) &&
        !(p.href === "/licence" && licence.data?.state === "open") &&
        (!p.requires ||
          [p.requires].flat().some((r) => hasPermission(session, r))),
  );
  const navigation: Navigation = allowed.map((p) => ({
    title: p.title,
    href: p.href,
  }));
  const fallback = locked
    ? "/licence"
    : allowed.some((p) => p.href === "/timeline")
      ? "/timeline"
      : "/review";
  const left = licence.data ? hoursLeft(licence.data) : null;
  const page = allowed.some((p) => p.href === pathname) ? pathname : fallback;
  const openAlerts = useOpenAlerts(
    !locked && hasPermission(session, "ViewAudit"),
  );

  return (
    <DashboardLayout
      branding={{ title: "AVGV" }}
      navigation={navigation}
      slots={{
        toolbarActions: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <UploadIndicator onOpen={() => goTo("/import")} />
            <ThemeSwitcher />
            <Typography variant="body2">{session.user.display_name}</Typography>
            <Button
              variant="outlined"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              Sign out
            </Button>
          </Box>
        ),
      }}
    >
      <Box sx={{ p: 3 }}>
        {(openAlerts.data?.length ?? 0) > 0 && page !== "/alerts" && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => goTo("/alerts")}
              >
                Review
              </Button>
            }
          >
            {openAlerts.data?.length} alert
            {openAlerts.data?.length === 1 ? "" : "s"} to review.
          </Alert>
        )}
        {left !== null && page !== "/licence" && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              hasPermission(session, "ManageSystem") && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => goTo("/licence")}
                >
                  Licence
                </Button>
              )
            }
          >
            AVGV evaluation:{" "}
            {left > 48
              ? `${Math.ceil(left / 24)} days`
              : `${left} hour${left === 1 ? "" : "s"}`}{" "}
            left.
          </Alert>
        )}
        {session.warnings.map((w) => (
          <Alert key={w} severity="warning" sx={{ mb: 2 }}>
            {w}
          </Alert>
        ))}
        {page === "/review" && <HomePage session={session} />}
        {page === "/timeline" && <TimelinePage />}
        {page === "/playback" && <PlaybackPage />}
        {page === "/cases" && <CasesPage />}
        {page === "/retention" && <RetentionPage />}
        {page === "/retention-settings" && <RetentionSettingsPage />}
        {page === "/import" && <ImportPage />}
        {page === "/needs-review" && <NeedsReviewPage />}
        {page === "/users" && <UsersPage />}
        {page === "/teams" && <TeamsPage />}
        {page === "/profiles" && <ProfilesPage />}
        {page === "/maps" && <MapsPage />}
        {page === "/devices" && <DevicesPage />}
        {page === "/analytics" && <AnalyticsPage />}
        {page === "/audit" && <AuditPage />}
        {page === "/alerts" && <AlertsPage />}
        {page === "/settings" && <SettingsPage />}
        {page === "/account" && <AccountPage />}
        {page === "/integrity" && <IntegrityPage />}
        {page === "/user-guide" && <HelpPage />}
        {page === "/about" && <AboutPage />}
        {page === "/licence" && (
          <LicencePage canManage={hasPermission(session, "ManageSystem")} />
        )}
      </Box>
    </DashboardLayout>
  );
}
